import { Component, ElementRef, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ApiError } from '../../../../core/errors/api-error';
import { MessageService } from '../../../../core/services/message/message.service';
import {
  ClienteCadastro,
  clienteCadastros
} from '../../../../home/pessoa/cliente/cadastro';
import { ClienteFilter } from '../../../../home/pessoa/cliente/filter/cliente-filter';
import {
  ClienteSituacao,
  clienteSituacoes
} from '../../../../home/pessoa/cliente/situacao/cliente-situacao';
import { IClientePessoa } from '../../../../home/pessoa/cliente/typeahead/cliente-pessoa.interface';
import { ClienteTypeahead } from '../../../../home/pessoa/cliente/typeahead/cliente-typeahead';
import { TypeaheadClienteHttpService } from '../../../../home/pessoa/cliente/typeahead/typeahead-cliente-http.service';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IAutocompleteSettings } from '../autocomplete/interface/settings.interface';

@Component({
  selector: 'sgt-novo-typeahead-cliente-central-compra',
  templateUrl: './novo-typeahead-cliente-central-compra.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoTypeaheadClienteCentralCompraComponent),
      multi: true
    }
  ]
})
export class NovoTypeaheadClienteCentralCompraComponent extends CustomControlValueAccessor<IClientePessoa> {
  clientes: IClientePessoa[] = [];
  selectedCliente: IClientePessoa | null = null;
  settings: IAutocompleteSettings = {
    placeholder: 'Digite o nome ou código da central de compras',
    noResultsText: 'Nenhuma central de compras encontrada',
    disabled: false,
    isLoading: false,
    showBeforeContent: true,
    isEmpty: null
  };

  constructor(
    private el: ElementRef,
    private messageService: MessageService,
    private typeaheadClienteHttpService: TypeaheadClienteHttpService
  ) {
    super();
  }

  setFocus(): void {
    const input = this.el.nativeElement.querySelector('input');
    if (input) {
      input.focus();
    }
  }

  private buildFilter(searchText: string): ClienteFilter {
    const filter = new ClienteFilter();
    filter.filterPsq = searchText;
    filter.somenteCentralDeCompra = true;
    return filter;
  }

  fetchClientes(searchText: string): void {
    this.settings.isLoading = true;
    this.typeaheadClienteHttpService
      .search(this.buildFilter(searchText), 1000)
      .subscribe(
        (entities: ClienteTypeahead[]) => {
          this.clientes = this.transformPayloadToAutocompleteItens(entities);
          this.setAutoCompleteSettings();
        },
        (error: ApiError) =>
          this.messageService.error(
            error.message,
            'Erro ao pesquisar as centrais de compras',
            error
          )
      );
  }

  setAutoCompleteSettings(): void {
    this.settings.isEmpty = this.clientes.length === 0;
    this.settings.isOpenDropdown = this.clientes.length > 0;
    this.settings.isLoading = false;

    if (this.settings.isEmpty) {
      this.selectedCliente = null;
    }
  }

  transformPayloadToAutocompleteItens(
    clientes: ClienteTypeahead[]
  ): IClientePessoa[] {
    return clientes.map((cliente) => ({
      id: cliente.id,
      idPessoa: cliente.idPessoa,
      codigo: cliente.pessoa?.codigo,
      nome: cliente.pessoa?.nome,
      nomeFantasia: cliente.pessoa?.nomeFantasia,
      situacao: cliente.situacao,
      nomeCidade: cliente?.nomeCidade,
      uf: cliente?.uf,
      cadastroLabel: this.getClienteCadastro(cliente.cadastro)?.nome,
      cssClassCadastro: this.getClienteCadastro(cliente.cadastro)?.badge,
      situacaoLabel: this.getClienteSituacao(cliente.situacao)?.nome,
      cssClassSituacao: this.getClienteSituacao(cliente.situacao)?.badge
    }));
  }

  getClienteCadastro(numeroCadastro: number): ClienteCadastro | null {
    return clienteCadastros.find((c) => c.id === numeroCadastro) || null;
  }

  getClienteSituacao(numeroSituacao: number): ClienteSituacao | null {
    return clienteSituacoes.find((s) => s.id === numeroSituacao) || null;
  }

  onSearchCliente(searchText: string): void {
    if (searchText && searchText.length) {
      this.fetchClientes(searchText);
      return;
    }

    this.clientes = [];
    this.selectedCliente = null;
    this.settings.isOpenDropdown = false;
    this.emitChange(null);
  }

  onSelectCliente(cliente: IClientePessoa): void {
    this.selectedCliente = cliente;
    this.settings.isOpenDropdown = false;
    this.clientes = [];
    this.emitChange(cliente);
  }

  protected updateValue(newValue: IClientePessoa | null): void {
    this.selectedCliente = newValue;
    if (!newValue) {
      this.settings = { ...this.settings, clearFields: true };
    }
  }
}
